import { useEffect, useRef, useState, useCallback } from "react";
import {
  newTextElement,
  CaptureUpdateAction,
  newElementWith,
  measureText,
  wrapText,
  newFrameElement,
  addElementsToFrame,
  getCommonBounds,
} from "@excalidraw/element";
import { getFontString } from "@excalidraw/common";

import type {
  ExcalidrawElement,
  ExcalidrawLuzmoChartElement,
  ExcalidrawTextElement,
} from "@excalidraw/element/types";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { useLuzmoChartContext } from "../context/LuzmoChartContext";
import { getLuzmoAuthConfig } from "../luzmo.config";

import type React from "react";

const SUMMARY_CHART_CUSTOM_DATA_KEY = "aiSummaryElementId";
const SUMMARY_FRAME_CUSTOM_DATA_KEY = "aiSummaryFrameId";
/** Chart ID stored on the summary frame so we can show chart settings when frame is selected */
export const SUMMARY_FRAME_CHART_ID_KEY = "aiSummaryChartId";

const MAX_FRAME_NAME_LENGTH = 40;

/**
 * Get a customer-facing name for the frame that contains the chart + AI summary.
 * Clearly indicates it's auto-generated by the chart with an AI summary.
 * Kept short for readability.
 */
function getChartSummaryFrameName(chart: ExcalidrawLuzmoChartElement): string {
  const title = chart.options?.title?.en?.trim();
  const base =
    title && title.length > 0
      ? title
      : chart.chartType
          .replace(/-/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
  const name = `${base} — Chart + AI Summary`;
  return name.length > MAX_FRAME_NAME_LENGTH
    ? `${name.slice(0, MAX_FRAME_NAME_LENGTH - 3)}…`
    : name;
}
const SUMMARY_OFFSET_Y = 48;
const FRAME_PADDING = 16;
const TYPEWRITER_SPEED_MS = 25;
const ELLIPSIS_ANIMATION_SPEED_MS = 400;

// Loading indicator base text (without ellipsis - will be animated)
const LOADING_TEXT_BASE = "Generating AI summary";
// Shown when filter changes and summary is being regenerated
const REGENERATING_TEXT_BASE = "Regenerating summary";

/**
 * Hook that returns an animated ellipsis string that cycles: "", ".", "..", "..."
 */
function useAnimatedEllipsis(isActive: boolean): string {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setDotCount(0);
      return;
    }

    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, ELLIPSIS_ANIMATION_SPEED_MS);

    return () => clearInterval(interval);
  }, [isActive]);

  return ".".repeat(dotCount);
}

/**
 * Get the current loading text with animated ellipsis
 */
function getLoadingText(ellipsis: string): string {
  return `${LOADING_TEXT_BASE}${ellipsis}`;
}

/**
 * Get the current regenerating text with animated ellipsis
 */
function getRegeneratingText(ellipsis: string): string {
  return `${REGENERATING_TEXT_BASE}${ellipsis}`;
}

/**
 * Slot content item structure from Luzmo
 */
interface SlotContentItem {
  label?: { en?: string; [key: string]: string | undefined };
  columnId?: string;
  type?: string;
}

/**
 * Slot structure from Luzmo
 */
interface LuzmoSlot {
  name: string;
  content: SlotContentItem[];
}

/**
 * Check if any slots have columns configured.
 * Returns true if at least one slot has content with columns.
 */
function hasColumnsInSlots(slotContent: unknown): boolean {
  if (!slotContent || !Array.isArray(slotContent)) {
    return false;
  }

  const slots = slotContent as LuzmoSlot[];
  return slots.some(
    (slot) => Array.isArray(slot.content) && slot.content.length > 0,
  );
}

/**
 * Extract column labels from slot content in order.
 * getData() returns values in the same order as columns are defined in slots.
 * Slots are ordered: dimensions first (category, x-axis, row, column, etc.), then measures.
 */
function extractColumnLabels(slotContent: unknown): string[] {
  if (!slotContent || !Array.isArray(slotContent)) {
    return [];
  }

  const labels: string[] = [];
  const slots = slotContent as LuzmoSlot[];

  // Common dimension slot names (these come first in getData output)
  const dimensionSlotNames = [
    "category",
    "x-axis",
    "row",
    "column",
    "hierarchy",
    "dimension",
  ];
  // Measure slot names (these come after dimensions)
  const measureSlotNames = ["measure", "y-axis", "value", "size"];

  // Process dimension slots first
  for (const slot of slots) {
    if (dimensionSlotNames.includes(slot.name) && Array.isArray(slot.content)) {
      for (const item of slot.content) {
        const label = item.label?.en || item.columnId || `${slot.name}`;
        labels.push(label);
      }
    }
  }

  // Then process measure slots
  for (const slot of slots) {
    if (measureSlotNames.includes(slot.name) && Array.isArray(slot.content)) {
      for (const item of slot.content) {
        const label = item.label?.en || item.columnId || `${slot.name}`;
        labels.push(label);
      }
    }
  }

  // Handle any remaining slots not in the known categories
  for (const slot of slots) {
    if (
      !dimensionSlotNames.includes(slot.name) &&
      !measureSlotNames.includes(slot.name) &&
      Array.isArray(slot.content)
    ) {
      for (const item of slot.content) {
        const label = item.label?.en || item.columnId || slot.name;
        labels.push(label);
      }
    }
  }

  return labels;
}

/**
 * Extract the display value from a dimension object or primitive.
 * Dimension objects have: id, name (multilingual: {en, nl, fr}), color, order
 */
function extractDisplayValue(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const obj = value as { name?: { en?: string }; id?: string };
    // If it's a dimension object with a name, extract the English label
    if (obj.name?.en) {
      return obj.name.en;
    }
    if (obj.id !== undefined) {
      return obj.id;
    }
  }
  return value;
}

/**
 * Transform chart data from getData() to the format expected by the Luzmo AI Summary API.
 * Uses slot content labels as keys for better AI context.
 *
 * @param chartData - Raw data from getData()
 * @param slotContent - Slot configuration containing column labels
 */
function transformChartData(
  chartData: unknown,
  slotContent?: unknown,
): Record<string, unknown>[] {
  if (!chartData || !Array.isArray(chartData)) {
    console.error("No chart data found");
    return [];
  }

  // Extract column labels from slot content
  const columnLabels = extractColumnLabels(slotContent);

  return chartData.map((row) => {
    if (!Array.isArray(row)) {
      // If it's already an object, return as-is
      if (typeof row === "object" && row !== null) {
        return row as Record<string, unknown>;
      }
      return { value: row };
    }

    const transformed: Record<string, unknown> = {};

    // Map each value to its corresponding column label
    row.forEach((value, index) => {
      const label = columnLabels[index] || `column_${index}`;
      transformed[label] = extractDisplayValue(value);
    });

    return transformed;
  });
}

// Message shown when chart has no data configured
export const NO_DATA_MESSAGE =
  "Add data to the chart to generate an AI summary.";

export type GenerateAISummaryResult =
  | { success: true; text: string }
  | { success: false; reason: string };

/**
 * Fetch AI summary from the Luzmo AI Summary API.
 *
 * @param chartType - The type of chart (e.g., "bar-chart", "line-chart")
 * @param chartData - Chart data from getData()
 * @param slotContent - Slot configuration from the chart
 * @returns Promise resolving to success/failure result with text or reason
 */
export const generateAISummary = async (
  chartId: string,
  chartType: string,
  chartData?: unknown,
  slotContent?: unknown,
): Promise<GenerateAISummaryResult> => {
  // Check if the chart has any columns configured in its slots
  if (!hasColumnsInSlots(slotContent)) {
    return { success: true, text: NO_DATA_MESSAGE };
  }

  const config = getLuzmoAuthConfig();

  // Transform chart data to the expected format, using slot labels as keys
  const transformedData = transformChartData(chartData, slotContent);

  // Luzmo API requires non-empty chart data - avoid 500 "Chart data is required"
  if (!transformedData || transformedData.length === 0) {
    return {
      success: true,
      text: "Chart has no data yet. Add data or wait for the chart to load.",
    };
  }

  const requestBody = {
    version: "0.1.0",
    action: "create",
    key: config.authKey,
    token: config.authToken,
    properties: {
      type: "chart",
      action: "generate",
      chart_type: chartType,
      locale: "en",
      stream: false,
      chart_data: transformedData,
      slot_content: slotContent || [],
      custom_prompt:
        "NEVER use Markdown formatting in the summary. NEVER mention the chart title in the summary.",
    },
  };

  try {
    const response = await fetch(`${config.apiHost}/0.1.0/aisummary`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `Chart summary API error: ${response.status} - ${errorText}`,
      );
      if (response.status === 429) {
        let retryMessage =
          "Rate limit exceeded. Please try again in a few moments.";
        try {
          const body = JSON.parse(errorText) as {
            retry_at?: number;
            message?: string;
          };
          if (typeof body.retry_at === "number") {
            const retryDate = new Date(body.retry_at);
            retryMessage = `Rate limit exceeded. Please retry after ${retryDate.toLocaleString()}.`;
          }
        } catch {
          // Use default message if parsing fails
        }
        return { success: false, reason: retryMessage };
      }
      // Parse error body for 500 and others to show API message when available
      let reason = `API error: ${response.status}. Please try again.`;
      try {
        const body = JSON.parse(errorText) as {
          error?: string;
          message?: string;
        };
        const msg = body?.error ?? body?.message;
        if (typeof msg === "string" && msg.length > 0) {
          reason = msg;
        }
      } catch {
        // Use default reason if parsing fails
      }
      return { success: false, reason };
    }

    const data = await response.json();
    const text = data?.text;
    return {
      success: true,
      text: typeof text === "string" ? text : "No summary generated.",
    };
  } catch (error) {
    console.error("Failed to generate AI summary:", error);
    return {
      success: false,
      reason: "Unable to generate summary. Please try again.",
    };
  }
};

interface TypewriterState {
  summaryElementId: string;
  fullText: string;
  currentLength: number;
  isComplete: boolean;
  isLoadingState?: boolean; // True when showing loading/regenerating text with animated ellipsis
}

const CACHED_SUMMARY_PREFIX = "Showing previous summary";

/** Apply fetch result to typewriter state; on failure with cache, render full text immediately */
function applySummaryResult(
  api: ExcalidrawImperativeAPI,
  chart: ExcalidrawLuzmoChartElement,
  result: GenerateAISummaryResult,
  state: TypewriterState,
  summaryCache: Record<string, string>,
): void {
  if (result.success) {
    state.fullText = result.text;
    state.currentLength = 0;
    state.isComplete = false;
    state.isLoadingState = false;
    return;
  }
  const cached = summaryCache[chart.id];
  const displayText = cached
    ? `${result.reason}\n\n${CACHED_SUMMARY_PREFIX}:\n\n${cached}`
    : result.reason;
  state.fullText = displayText;
  state.currentLength = displayText.length;
  state.isComplete = true;
  state.isLoadingState = false;
  const elements = api.getSceneElements();
  const summaryEl = elements.find((el) => el.id === state.summaryElementId);
  if (summaryEl?.type === "text") {
    const textEl = summaryEl as ExcalidrawTextElement;
    const wrapped = wrapText(displayText, getFontString(textEl), textEl.width);
    const metrics = measureText(
      wrapped,
      getFontString(textEl),
      textEl.lineHeight,
    );
    api.mutateElement(textEl, {
      text: wrapped,
      originalText: displayText,
      height: metrics.height,
    });
    // Resize frame to fit the full text (no typewriter animation)
    const frameId = chart.customData?.[SUMMARY_FRAME_CUSTOM_DATA_KEY];
    if (frameId) {
      const frame = elements.find(
        (el) =>
          (el.type === "frame" || el.type === "magicframe") &&
          el.id === frameId,
      );
      if (frame) {
        const [x1, y1, x2, y2] = getCommonBounds([
          chart as ExcalidrawElement,
          summaryEl,
        ]);
        api.mutateElement(frame, {
          x: x1 - FRAME_PADDING,
          y: y1 - FRAME_PADDING,
          width: x2 - x1 + FRAME_PADDING * 2,
          height: y2 - y1 + FRAME_PADDING * 2,
        });
      }
    }
  }
}

/**
 * Manages AI summary text elements for Luzmo charts. When a chart has aiSummaryEnabled
 * and has emitted "rendered" + getData, creates a real Excalidraw text element below
 * the chart and runs a typewriter effect. The summary is a separate element so users
 * can move it around.
 */
export const LuzmoChartSummaryManager: React.FC = () => {
  const context = useLuzmoChartContext();
  const [sceneVersion, setSceneVersion] = useState(0);

  // Track typewriter state for each chart
  const typewriterStateRef = useRef<Record<string, TypewriterState>>({});

  // Track which charts are currently fetching summaries
  const fetchingRef = useRef<Set<string>>(new Set());
  // Charts we've refreshed on load this session (avoids re-fetch loop when effect re-runs)
  const refreshedOnLoadRef = useRef<Set<string>>(new Set());
  // Cache of last successful summary per chart - used when API fails (e.g. rate limit)
  const summaryCacheRef = useRef<Record<string, string>>({});

  // Track which charts are showing loading/regenerating state
  const [loadingCharts, setLoadingCharts] = useState<Set<string>>(new Set());

  // Animated ellipsis for loading states
  const hasLoadingCharts = loadingCharts.size > 0;
  const animatedEllipsis = useAnimatedEllipsis(hasLoadingCharts);

  // Re-run creation logic when scene changes (e.g. user toggles aiSummaryEnabled)
  useEffect(() => {
    const api = context?.excalidrawAPI;
    if (!api) {
      return;
    }
    const unsub = api.onChange(() => setSceneVersion((v) => v + 1));
    return unsub;
  }, [context?.excalidrawAPI]);

  // Fetch AI summary for a chart (optionally with getData for live-data display)
  const fetchSummary = useCallback(
    async (
      chart: ExcalidrawLuzmoChartElement,
      chartData?: unknown,
    ): Promise<GenerateAISummaryResult> => {
      if (fetchingRef.current.has(chart.id)) {
        return { success: false, reason: "Already fetching." };
      }

      fetchingRef.current.add(chart.id);
      try {
        const result = await generateAISummary(
          chart.id,
          chart.chartType,
          chartData,
          chart.slots,
        );
        if (result.success) {
          summaryCacheRef.current[chart.id] = result.text;
        }
        return result;
      } finally {
        fetchingRef.current.delete(chart.id);
      }
    },
    [],
  );

  // Handler ref - we update this when context changes; the stable wrapper invokes it
  const changedFiltersHandlerRef = useRef<
    ((sourceElementId: string) => void) | null
  >(null);
  const contextRef = useRef(context);
  contextRef.current = context;

  // Track if we've registered the callbacks
  const callbackRegisteredRef = useRef(false);

  // Handler ref for slots changes
  const slotsChangedHandlerRef = useRef<((elementId: string) => void) | null>(
    null,
  );

  // Register stable wrappers once when context becomes available
  useEffect(() => {
    // Only register once
    if (callbackRegisteredRef.current || !context) {
      return;
    }

    const stableFilterWrapper = (sourceElementId: string) => {
      changedFiltersHandlerRef.current?.(sourceElementId);
    };
    context.setOnChangedFiltersCallback(stableFilterWrapper);

    const stableSlotsWrapper = (elementId: string) => {
      slotsChangedHandlerRef.current?.(elementId);
    };
    context.setOnSlotsChangedCallback(stableSlotsWrapper);

    callbackRegisteredRef.current = true;
  }); // No deps - we use ref to track registration

  // React to changedFilters: refresh summaries of OTHER charts (not the source) after their getData resolves.
  // Update handler ref - the stable wrapper (registered once above) invokes this ref
  useEffect(() => {
    if (!context) {
      return;
    }
    const handler = async (sourceElementId: string) => {
      const ctx = contextRef.current;
      if (!ctx) {
        return;
      }
      const api = ctx.excalidrawAPI;
      if (!api) {
        return;
      }

      const elements = api.getSceneElementsIncludingDeleted();
      const charts = elements.filter(
        (el) => el.type === "luzmochart" && !el.isDeleted,
      ) as ExcalidrawLuzmoChartElement[];

      for (const chart of charts) {
        if (chart.id === sourceElementId || !chart.aiSummaryEnabled) {
          continue;
        }

        // Check if chart has getData registered (meaning it has rendered at least once)
        const getDataFn = ctx.getChartGetData(chart.id);
        if (!getDataFn) {
          continue;
        }

        try {
          // Mark chart as loading for animated ellipsis
          setLoadingCharts((prev) => new Set(prev).add(chart.id));

          // Clear summary and show "Regenerating summary..." immediately
          const state = typewriterStateRef.current[chart.id];
          const regeneratingText = getRegeneratingText("");
          if (state) {
            const summaryEl = api
              .getSceneElements()
              .find((el) => el.id === state.summaryElementId) as
              | ExcalidrawTextElement
              | undefined;
            if (summaryEl?.type === "text") {
              const wrapped = wrapText(
                regeneratingText,
                getFontString(summaryEl),
                summaryEl.width,
              );
              const metrics = measureText(
                wrapped,
                getFontString(summaryEl),
                summaryEl.lineHeight,
              );
              api.mutateElement(summaryEl, {
                text: wrapped,
                originalText: regeneratingText,
                height: metrics.height,
              });
            }
            typewriterStateRef.current[chart.id] = {
              ...state,
              fullText: regeneratingText,
              currentLength: regeneratingText.length,
              isComplete: false, // Not complete - will animate ellipsis
              isLoadingState: true, // Flag to indicate this is a loading/regenerating state
            };
          } else {
            // Recreate typewriter state if missing (e.g. edge case on initial render)
            const summaryId = chart.customData?.[SUMMARY_CHART_CUSTOM_DATA_KEY];
            const summaryEl = summaryId
              ? (elements.find((el) => el.id === summaryId && !el.isDeleted) as
                  | ExcalidrawTextElement
                  | undefined)
              : undefined;
            if (summaryEl?.type === "text") {
              typewriterStateRef.current[chart.id] = {
                summaryElementId: summaryEl.id,
                fullText: regeneratingText,
                currentLength: regeneratingText.length,
                isComplete: false,
                isLoadingState: true,
              };
            }
          }

          // IMPORTANT: Wait for the chart to re-render with filtered data before calling getData.
          // When a filter changes, charts need to re-query and re-render. The `rendered` event
          // fires AFTER the chart has finished rendering with the new data.
          // Using registerPendingGetData ensures we get fresh data, not stale pre-filter data.
          const chartData = await new Promise<unknown>((resolve) => {
            // Set a timeout in case the chart doesn't re-render (e.g., filter doesn't affect it)
            const timeoutId = setTimeout(() => {
              // Fallback: use current getData if chart doesn't re-render within 5 seconds
              getDataFn()
                .then(resolve)
                .catch(() => resolve([]));
            }, 5000);

            ctx.registerPendingGetData(chart.id, (data) => {
              clearTimeout(timeoutId);
              resolve(data);
            });
          });

          const result = await fetchSummary(chart, chartData);
          const twState = typewriterStateRef.current[chart.id];
          if (
            twState &&
            (result.success || result.reason !== "Already fetching.")
          ) {
            applySummaryResult(
              api,
              chart,
              result,
              twState,
              summaryCacheRef.current,
            );
          }
          // Remove from loading charts
          setLoadingCharts((prev) => {
            const next = new Set(prev);
            next.delete(chart.id);
            return next;
          });
        } catch (err) {
          console.error(
            "[SummaryManager] Failed to refresh after changedFilters:",
            err,
          );
          // Remove from loading charts on error too
          setLoadingCharts((prev) => {
            const next = new Set(prev);
            next.delete(chart.id);
            return next;
          });
        }
      }
    };
    changedFiltersHandlerRef.current = handler;
  }); // No deps - always update the ref with latest handler

  // React to slots changes: refresh summary of the chart whose slots changed
  useEffect(() => {
    if (!context) {
      return;
    }
    const handler = async (elementId: string) => {
      const ctx = contextRef.current;
      if (!ctx) {
        return;
      }
      const api = ctx.excalidrawAPI;
      if (!api) {
        return;
      }

      const elements = api.getSceneElementsIncludingDeleted();
      const chart = elements.find(
        (el) =>
          el.id === elementId && el.type === "luzmochart" && !el.isDeleted,
      ) as ExcalidrawLuzmoChartElement | undefined;

      if (!chart || !chart.aiSummaryEnabled) {
        return;
      }

      // Check if chart has getData registered (meaning it has rendered at least once)
      const getDataFn = ctx.getChartGetData(chart.id);
      if (!getDataFn) {
        return;
      }

      try {
        // Mark chart as loading for animated ellipsis
        setLoadingCharts((prev) => new Set(prev).add(chart.id));

        // Clear summary and show "Regenerating summary..." immediately
        const state = typewriterStateRef.current[chart.id];
        const regeneratingText = getRegeneratingText("");
        if (state) {
          const summaryEl = api
            .getSceneElements()
            .find((el) => el.id === state.summaryElementId) as
            | ExcalidrawTextElement
            | undefined;
          if (summaryEl?.type === "text") {
            const wrapped = wrapText(
              regeneratingText,
              getFontString(summaryEl),
              summaryEl.width,
            );
            const metrics = measureText(
              wrapped,
              getFontString(summaryEl),
              summaryEl.lineHeight,
            );
            api.mutateElement(summaryEl, {
              text: wrapped,
              originalText: regeneratingText,
              height: metrics.height,
            });
          }
          typewriterStateRef.current[chart.id] = {
            ...state,
            fullText: regeneratingText,
            currentLength: regeneratingText.length,
            isComplete: false,
            isLoadingState: true,
          };
        }

        // Wait for the chart to re-render with new slot configuration
        const chartData = await new Promise<unknown>((resolve) => {
          // Set a timeout in case the chart doesn't re-render
          const timeoutId = setTimeout(() => {
            // Fallback: use current getData if chart doesn't re-render within 5 seconds
            getDataFn()
              .then(resolve)
              .catch(() => resolve([]));
          }, 5000);

          ctx.registerPendingGetData(chart.id, (data) => {
            clearTimeout(timeoutId);
            resolve(data);
          });
        });

        const result = await fetchSummary(chart, chartData);
        const twState = typewriterStateRef.current[chart.id];
        if (
          twState &&
          (result.success || result.reason !== "Already fetching.")
        ) {
          applySummaryResult(
            api,
            chart,
            result,
            twState,
            summaryCacheRef.current,
          );
        }
        // Remove from loading charts
        setLoadingCharts((prev) => {
          const next = new Set(prev);
          next.delete(chart.id);
          return next;
        });
      } catch (err) {
        console.error(
          "[SummaryManager] Failed to refresh after slots change:",
          err,
        );
        // Remove from loading charts on error too
        setLoadingCharts((prev) => {
          const next = new Set(prev);
          next.delete(chart.id);
          return next;
        });
      }
    };
    slotsChangedHandlerRef.current = handler;
  }); // No deps - always update the ref with latest handler

  // Create or update summary elements
  useEffect(() => {
    const api = context?.excalidrawAPI;
    if (!api) {
      return;
    }

    const elements = api.getSceneElementsIncludingDeleted();
    const charts = elements.filter(
      (el) => el.type === "luzmochart" && !el.isDeleted,
    ) as ExcalidrawLuzmoChartElement[];

    for (const chart of charts) {
      // If AI summary is disabled, clean up the summary element
      if (!chart.aiSummaryEnabled) {
        // Clean up typewriter state and cache if summary was disabled
        if (typewriterStateRef.current[chart.id]) {
          delete typewriterStateRef.current[chart.id];
        }
        delete summaryCacheRef.current[chart.id];

        // Delete the summary element and frame if they exist
        const existingSummaryId =
          chart.customData?.[SUMMARY_CHART_CUSTOM_DATA_KEY];
        const existingFrameId =
          chart.customData?.[SUMMARY_FRAME_CUSTOM_DATA_KEY];
        if (existingSummaryId || existingFrameId) {
          const summaryElement = existingSummaryId
            ? elements.find(
                (el) => el.id === existingSummaryId && !el.isDeleted,
              )
            : null;
          const frameElement = existingFrameId
            ? elements.find(
                (el) =>
                  el.id === existingFrameId &&
                  (el.type === "frame" || el.type === "magicframe") &&
                  !el.isDeleted,
              )
            : null;

          if (summaryElement || frameElement) {
            // Remove chart from frame and clear customData
            const updatedChart = newElementWith(chart, {
              frameId: null,
              customData: {
                ...chart.customData,
                [SUMMARY_CHART_CUSTOM_DATA_KEY]: undefined,
                [SUMMARY_FRAME_CUSTOM_DATA_KEY]: undefined,
              },
            });

            const nextElements = elements.map((el) => {
              if (el.id === chart.id) {
                return updatedChart;
              }
              if (existingSummaryId && el.id === existingSummaryId) {
                return newElementWith(el, { isDeleted: true });
              }
              if (existingFrameId && el.id === existingFrameId) {
                return newElementWith(el, { isDeleted: true });
              }
              return el;
            }) as ExcalidrawElement[];

            api.updateScene({
              elements: nextElements,
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        }
        continue;
      }

      // Note: We no longer require chartReady - create summary immediately when enabled
      // The chart data can be fetched later if needed for AI analysis

      const existingSummaryId =
        chart.customData?.[SUMMARY_CHART_CUSTOM_DATA_KEY];
      const existingSummaryElement = existingSummaryId
        ? elements.find((el) => el.id === existingSummaryId && !el.isDeleted)
        : null;

      // If summary element already exists and typewriter is complete and we've refreshed, skip
      const getDataFn = context?.getChartGetData(chart.id);

      if (
        existingSummaryElement &&
        typewriterStateRef.current[chart.id]?.isComplete &&
        refreshedOnLoadRef.current.has(chart.id)
      ) {
        continue;
      }

      // If summary element exists but we haven't refreshed yet (fresh page load),
      // show "Generating AI summary..." and fetch when getData becomes available
      if (
        existingSummaryElement &&
        existingSummaryElement.type === "text" &&
        !refreshedOnLoadRef.current.has(chart.id)
      ) {
        const textEl = existingSummaryElement as ExcalidrawTextElement;
        const loadingText = getLoadingText("");

        // Initialize typewriter state if not present - show loading text immediately
        if (!typewriterStateRef.current[chart.id]) {
          setLoadingCharts((prev) => new Set(prev).add(chart.id));
          typewriterStateRef.current[chart.id] = {
            summaryElementId: textEl.id,
            fullText: loadingText,
            currentLength: 0,
            isComplete: false,
            isLoadingState: true,
          };
        }

        // If getData is now available (chart has rendered), fetch the actual summary
        if (getDataFn) {
          refreshedOnLoadRef.current.add(chart.id);
          (async () => {
            let chartData: unknown;
            try {
              chartData = await getDataFn();
            } catch {
              chartData = undefined;
            }
            const result = await fetchSummary(chart, chartData);
            const twState = typewriterStateRef.current[chart.id];
            if (
              twState &&
              (result.success || result.reason !== "Already fetching.")
            ) {
              applySummaryResult(
                api,
                chart,
                result,
                twState,
                summaryCacheRef.current,
              );
            }
            // Remove from loading charts
            setLoadingCharts((prev) => {
              const next = new Set(prev);
              next.delete(chart.id);
              return next;
            });
          })();
        }
        continue;
      }

      // If no summary element exists (or the referenced element was deleted), create one
      // This handles both: 1) no ID set yet, 2) ID set but element doesn't exist
      if (!existingSummaryElement) {
        const loadingText = getLoadingText("");
        // Create text element with initial text
        const baseSummaryEl = newTextElement({
          x: chart.x,
          y: chart.y + chart.height + SUMMARY_OFFSET_Y,
          text: loadingText[0],
          originalText: loadingText[0],
          fontSize: 16,
          frameId: null, // Will be set when we add to frame
          autoResize: false, // Disable auto-resize so we can set fixed width
        });

        // Set the width to match the chart width
        const summaryEl = newElementWith(baseSummaryEl, {
          width: chart.width,
        });

        // Create a frame that encapsulates the chart and summary together.
        // Frame has no own settings - clicking it opens the Luzmo chart settings.
        const [x1, y1, x2, y2] = getCommonBounds([chart, summaryEl]);
        const frame = newFrameElement({
          x: x1 - FRAME_PADDING,
          y: y1 - FRAME_PADDING,
          width: x2 - x1 + FRAME_PADDING * 2,
          height: y2 - y1 + FRAME_PADDING * 2,
          name: getChartSummaryFrameName(chart),
          customData: { [SUMMARY_FRAME_CHART_ID_KEY]: chart.id },
        });

        const updatedChart = newElementWith(chart, {
          customData: {
            ...chart.customData,
            [SUMMARY_CHART_CUSTOM_DATA_KEY]: summaryEl.id,
            [SUMMARY_FRAME_CUSTOM_DATA_KEY]: frame.id,
          },
        });

        // Insert frame first (renders behind), then chart, then summary (on top)
        // Elements are rendered in array order, so later = on top
        const chartIdx = elements.findIndex((el) => el.id === chart.id);
        const elementsWithoutChart = elements.filter(
          (el) => el.id !== chart.id,
        );
        const nextElements = [
          ...elementsWithoutChart.slice(0, chartIdx),
          frame,
          updatedChart,
          summaryEl,
          ...elementsWithoutChart.slice(chartIdx),
        ] as ExcalidrawElement[];

        const appState = api.getAppState();
        addElementsToFrame(
          nextElements,
          [updatedChart, summaryEl],
          frame,
          appState,
        );

        api.updateScene({
          elements: nextElements,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Mark as loading for animated ellipsis
        setLoadingCharts((prev) => new Set(prev).add(chart.id));

        // Initialize typewriter state with loading text
        typewriterStateRef.current[chart.id] = {
          summaryElementId: summaryEl.id,
          fullText: loadingText,
          currentLength: 1,
          isComplete: false,
          isLoadingState: true,
        };

        // Fetch actual summary in background (include getData if chart has registered it)
        (async () => {
          let chartData: unknown;
          try {
            const getDataFn = context?.getChartGetData(chart.id);
            chartData = getDataFn ? await getDataFn() : undefined;
          } catch {
            chartData = undefined;
          }
          const result = await fetchSummary(chart, chartData);
          const twState = typewriterStateRef.current[chart.id];
          if (
            twState &&
            (result.success || result.reason !== "Already fetching.")
          ) {
            applySummaryResult(
              api,
              chart,
              result,
              twState,
              summaryCacheRef.current,
            );
          }
          // Remove from loading charts
          setLoadingCharts((prev) => {
            const next = new Set(prev);
            next.delete(chart.id);
            return next;
          });
        })();
      }
    }
  }, [
    context,
    context?.excalidrawAPI,
    context?.chartReadyMap,
    context?.getDataVersion,
    sceneVersion,
    fetchSummary,
  ]);

  // Animated ellipsis effect for loading states
  useEffect(() => {
    const api = context?.excalidrawAPI;
    if (!api || loadingCharts.size === 0) {
      return;
    }

    for (const chartId of loadingCharts) {
      const state = typewriterStateRef.current[chartId];
      if (!state || !state.isLoadingState) {
        continue;
      }

      const elements = api.getSceneElements();
      const summaryEl = elements.find((el) => el.id === state.summaryElementId);
      if (!summaryEl || summaryEl.type !== "text") {
        continue;
      }

      // Determine if this is loading or regenerating based on the base text
      const isRegenerating =
        typeof state.fullText === "string" &&
        state.fullText.startsWith(REGENERATING_TEXT_BASE);
      const newText = isRegenerating
        ? getRegeneratingText(animatedEllipsis)
        : getLoadingText(animatedEllipsis);

      // Update the fullText in state so typewriter knows the current text
      state.fullText = newText;
      state.currentLength = newText.length;

      // Wrap text to fit width and measure height
      const textEl = summaryEl as ExcalidrawTextElement;
      const wrapped = wrapText(newText, getFontString(textEl), textEl.width);
      const metrics = measureText(
        wrapped,
        getFontString(textEl),
        textEl.lineHeight,
      );

      api.mutateElement(textEl, {
        text: wrapped,
        originalText: newText,
        height: metrics.height,
      });
    }
  }, [context?.excalidrawAPI, loadingCharts, animatedEllipsis]);

  // Typewriter effect interval
  useEffect(() => {
    const api = context?.excalidrawAPI;
    if (!api) {
      return;
    }

    const interval = setInterval(() => {
      const states = typewriterStateRef.current;
      const stateKeys = Object.keys(states);
      if (stateKeys.length === 0) {
        return;
      }

      for (const chartId of stateKeys) {
        const state = states[chartId];
        if (!state || state.isComplete || state.isLoadingState) {
          continue;
        }

        const elements = api.getSceneElements();
        const summaryEl = elements.find(
          (el) => el.id === state.summaryElementId,
        );
        if (!summaryEl || summaryEl.type !== "text") {
          continue;
        }

        // Check if we've typed all characters
        const fullTextStr =
          typeof state.fullText === "string" ? state.fullText : "";
        if (state.currentLength >= fullTextStr.length) {
          state.isComplete = true;
          continue;
        }

        // Build the new text (from scratch to handle summary replacement)
        const newText = fullTextStr.slice(0, state.currentLength + 1);

        // Wrap text to fit width and measure height so element grows with content
        const textEl = summaryEl as ExcalidrawTextElement;
        const wrapped = wrapText(newText, getFontString(textEl), textEl.width);
        const metrics = measureText(
          wrapped,
          getFontString(textEl),
          textEl.lineHeight,
        );

        // Track previous height to detect line changes
        const previousHeight = textEl.height;

        api.mutateElement(textEl, {
          text: wrapped,
          originalText: newText,
          height: metrics.height,
        });

        // Resize frame smoothly when text height changes (new line added)
        if (metrics.height !== previousHeight) {
          const chart = elements.find(
            (el) => el.type === "luzmochart" && el.id === chartId,
          );
          const frameId = chart?.customData?.[SUMMARY_FRAME_CUSTOM_DATA_KEY];
          if (chart && frameId) {
            const frame = elements.find(
              (el) =>
                (el.type === "frame" || el.type === "magicframe") &&
                el.id === frameId,
            );
            if (frame) {
              // Calculate bounds manually using the new height
              // since getCommonBounds may not reflect the mutated height immediately
              const chartX1 = chart.x;
              const chartY1 = chart.y;
              const chartX2 = chart.x + chart.width;
              const chartY2 = chart.y + chart.height;
              const summaryX1 = textEl.x;
              const summaryY1 = textEl.y;
              const summaryX2 = textEl.x + textEl.width;
              const summaryY2 = textEl.y + metrics.height; // Use new height directly
              const x1 = Math.min(chartX1, summaryX1);
              const y1 = Math.min(chartY1, summaryY1);
              const x2 = Math.max(chartX2, summaryX2);
              const y2 = Math.max(chartY2, summaryY2);
              api.mutateElement(frame, {
                x: x1 - FRAME_PADDING,
                y: y1 - FRAME_PADDING,
                width: x2 - x1 + FRAME_PADDING * 2,
                height: y2 - y1 + FRAME_PADDING * 2,
              });
            }
          }
        }

        state.currentLength += 1;
      }
    }, TYPEWRITER_SPEED_MS);

    return () => clearInterval(interval);
  }, [context?.excalidrawAPI]);

  return null;
};
